(function() {
  if (window.__lacePackBuilderScriptLoaded) {
    return;
  }

  window.__lacePackBuilderScriptLoaded = true;

  function parseJson(node, fallback) {
    if (!node) {
      return fallback;
    }

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Invalid lace pack builder JSON', error);
      return fallback;
    }
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }

    return cents;
  }

  function initQtySelector(wrapper) {
    if (!wrapper || wrapper.dataset.qtyInitialized === 'true') {
      return;
    }

    if (window.theme && typeof theme.QtySelector === 'function') {
      new theme.QtySelector(wrapper);
      wrapper.dataset.qtyInitialized = 'true';
    }
  }

  function initModal(modal) {
    if (!modal || modal.dataset.modalInitialized === 'true') {
      return;
    }

    if (!window.theme || !theme.Modals) {
      return;
    }

    const modalName = modal.dataset.modalName;

    if (!modal.id || !modalName) {
      return;
    }

    new theme.Modals(modal.id, modalName);
    modal.dataset.modalInitialized = 'true';
  }

  class LacePackBuilder extends HTMLElement {
    constructor() {
      super();

      this.state = {
        rows: []
      };
      this.products = [];
      this.productsById = new Map();
      this.discountTiers = [];
      this.summary = null;
      this.rowIndex = 0;
      this.isSubmitting = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
    }

    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.config = parseJson(this.querySelector('[data-lace-pack-builder-config]'), {});
      this.products = parseJson(this.querySelector('[data-lace-pack-builder-products]'), []);
      this.products.forEach((product) => {
        this.productsById.set(String(product.id), product);
      });
      this.discountTiers = this.parseDiscountTiers(this.config.discountTiers);

      this.cacheElements();
      initModal(this.modal);
      this.bindEvents();
      this.buildProgressTrack();
      this.updateDerivedState();

      this.initialized = true;
    }

    cacheElements() {
      this.itemsContainer = this.querySelector('[data-lace-pack-builder-items]');
      this.progressNote = this.querySelector('[data-lace-pack-builder-progress-note]');
      this.progressTrack = this.querySelector('[data-lace-pack-builder-progress-track]');
      this.summaryWrap = this.querySelector('[data-lace-pack-builder-summary-wrap]');
      this.submitButton = this.querySelector('[data-lace-pack-builder-submit]');
      this.submitWrap = this.querySelector('[data-lace-pack-builder-submit-wrap]');
      this.statusElement = this.querySelector('[data-lace-pack-builder-status]');
      this.modal = this.querySelector('.js-lace-pack-builder-modal');
    }

    bindEvents() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('input', this.handleInput);
    }

    handleClick(event) {
      const productTrigger = event.target.closest('[data-lace-pack-builder-product-trigger]');
      if (productTrigger && this.contains(productTrigger)) {
        this.addProductRow(productTrigger.dataset.productId);
        this.closeModal();
        return;
      }

      const removeButton = event.target.closest('[data-lace-pack-builder-remove]');
      if (removeButton && this.contains(removeButton)) {
        this.removeRow(removeButton.dataset.rowId);
        return;
      }

      const submitButton = event.target.closest('[data-lace-pack-builder-submit]');
      if (submitButton && this.contains(submitButton)) {
        this.submitRows();
        return;
      }

      const qtyAdjustButton = event.target.closest('.js-qty__adjust');
      if (qtyAdjustButton && this.contains(qtyAdjustButton)) {
        const rowElement = qtyAdjustButton.closest('[data-row-id]');
        window.setTimeout(() => {
          this.syncRowQuantityFromDom(rowElement);
        }, 0);
      }
    }

    handleChange(event) {
      const variantSelect = event.target.closest('[data-lace-pack-builder-variant]');
      if (variantSelect && this.contains(variantSelect)) {
        const rowElement = variantSelect.closest('[data-row-id]');
        this.updateRowVariant(rowElement?.dataset.rowId, variantSelect.value);
        return;
      }

      const quantityInput = event.target.closest('.js-qty__num');
      if (quantityInput && this.contains(quantityInput)) {
        const rowElement = quantityInput.closest('[data-row-id]');
        this.syncRowQuantityFromDom(rowElement);
      }
    }

    handleInput(event) {
      const quantityInput = event.target.closest('.js-qty__num');
      if (quantityInput && this.contains(quantityInput)) {
        const rowElement = quantityInput.closest('[data-row-id]');
        this.syncRowQuantityFromDom(rowElement);
      }
    }

    parseDiscountTiers(rawValue) {
      return String(rawValue || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const parts = line.split('|').map((part) => part.trim());
          return {
            quantity: Number(parts[0]),
            discount: Number(parts[1])
          };
        })
        .filter((tier) => Number.isFinite(tier.quantity) && tier.quantity > 0 && Number.isFinite(tier.discount) && tier.discount >= 0)
        .sort((left, right) => left.quantity - right.quantity);
    }

    buildProgressTrack() {
      if (!this.progressTrack) {
        return;
      }

      this.progressTrack.innerHTML = '';

      const initialStep = document.createElement('div');
      initialStep.className = 'lace-pack-builder__progress-step is-active';
      initialStep.dataset.progressInitial = 'true';
      initialStep.innerHTML = '<div class="lace-pack-builder__progress-dot"></div>';
      this.progressTrack.appendChild(initialStep);

      let previousQuantity = 0;

      this.discountTiers.forEach((tier) => {
        const line = document.createElement('div');
        line.className = 'lace-pack-builder__progress-line';
        line.dataset.tierStart = String(previousQuantity);
        line.dataset.tierEnd = String(tier.quantity);
        line.style.setProperty('--lace-pack-builder-progress', '0%');
        this.progressTrack.appendChild(line);

        const step = document.createElement('div');
        step.className = 'lace-pack-builder__progress-step';
        step.dataset.tierQuantity = String(tier.quantity);
        step.innerHTML = `
          <div class="lace-pack-builder__progress-dot"></div>
          <div class="lace-pack-builder__progress-label">${escapeHtml(`${tier.discount}% OFF`)}</div>
        `;
        this.progressTrack.appendChild(step);

        previousQuantity = tier.quantity;
      });
    }

    getDefaultVariant(product) {
      if (!product || !Array.isArray(product.variants) || !product.variants.length) {
        return null;
      }

      return product.variants.find((variant) => variant.available) || product.variants[0];
    }

    addProductRow(productId) {
      const product = this.productsById.get(String(productId));
      const defaultVariant = this.getDefaultVariant(product);

      if (!product || !defaultVariant) {
        return;
      }

      this.rowIndex += 1;
      this.state.rows.push({
        id: `row-${this.rowIndex}`,
        productId: String(product.id),
        variantId: String(defaultVariant.id),
        quantity: 1
      });

      this.renderRows();
    }

    removeRow(rowId) {
      this.state.rows = this.state.rows.filter((row) => row.id !== rowId);
      this.renderRows();
    }

    updateRowVariant(rowId, variantId) {
      const row = this.state.rows.find((entry) => entry.id === rowId);
      if (!row) {
        return;
      }

      row.variantId = String(variantId);
      const rowElement = this.itemsContainer?.querySelector(`[data-row-id="${row.id}"]`);
      this.applyRowDatasets(rowElement, row);
      this.updateDerivedState();
    }

    syncRowQuantityFromDom(rowElement) {
      if (!rowElement) {
        return;
      }

      const row = this.state.rows.find((entry) => entry.id === rowElement.dataset.rowId);
      const input = rowElement.querySelector('.js-qty__num');

      if (!row || !input) {
        return;
      }

      const quantity = Math.max(1, Number(input.value) || 1);
      row.quantity = quantity;
      input.value = quantity;
      this.applyRowDatasets(rowElement, row);
      this.updateDerivedState();
    }

    getProductForRow(row) {
      return this.productsById.get(String(row.productId));
    }

    getVariantForRow(row) {
      const product = this.getProductForRow(row);
      return product?.variants?.find((variant) => String(variant.id) === String(row.variantId)) || this.getDefaultVariant(product);
    }

    getRowPricing(row, discountPercent) {
      const variant = this.getVariantForRow(row);
      const quantity = Math.max(1, Number(row.quantity) || 1);
      const unitPriceCents = Number(variant?.price || 0);
      const compareUnitPriceCents = Number(variant?.compareAtPrice || unitPriceCents);
      const subtotalCents = unitPriceCents * quantity;
      const compareSubtotalCents = compareUnitPriceCents * quantity;
      const bundleDiscountCents = Math.round((subtotalCents * discountPercent) / 100);
      const discountedSubtotalCents = subtotalCents - bundleDiscountCents;

      return {
        unitPriceCents,
        compareUnitPriceCents,
        subtotalCents,
        compareSubtotalCents,
        bundleDiscountCents,
        discountedSubtotalCents
      };
    }

    getActiveTier(totalQuantity) {
      return [...this.discountTiers].reverse().find((tier) => totalQuantity >= tier.quantity) || null;
    }

    getNextTier(totalQuantity) {
      return this.discountTiers.find((tier) => totalQuantity < tier.quantity) || null;
    }

    renderRows() {
      if (!this.itemsContainer) {
        return;
      }

      this.itemsContainer.innerHTML = this.state.rows.map((row) => this.renderRowMarkup(row)).join('');

      this.itemsContainer.querySelectorAll('.js-qty__wrapper').forEach((wrapper) => {
        initQtySelector(wrapper);
      });

      this.itemsContainer.querySelectorAll('[data-row-id]').forEach((rowElement) => {
        const row = this.state.rows.find((entry) => entry.id === rowElement.dataset.rowId);
        this.applyRowDatasets(rowElement, row);
      });

      this.updateDerivedState();
    }

    renderRowMarkup(row) {
      const product = this.getProductForRow(row);
      const variant = this.getVariantForRow(row);
      if (!product || !variant) {
        return '';
      }

      const pricing = this.getRowPricing(row, this.summary?.discountPercent || 0);
      const selectorLabel = product.hasOnlyDefaultVariant ? '' : product.selectorLabel || 'Variant';
      const selectorClass = selectorLabel ? 'lace-pack-builder__select-wrap' : 'lace-pack-builder__select-wrap lace-pack-builder__select-wrap--no-label';
      const selectorStyle = selectorLabel ? ` style="--lace-pack-builder-label-width: ${selectorLabel.length + 1}ch;"` : '';
      const variantOptions = product.variants.map((optionVariant) => {
        const optionTitle = optionVariant.title === 'Default Title' ? product.title : optionVariant.title;
        const selected = String(optionVariant.id) === String(row.variantId) ? ' selected="selected"' : '';
        const disabled = optionVariant.available ? '' : ' disabled="disabled"';
        return `<option value="${escapeHtml(optionVariant.id)}"${selected}${disabled}>${escapeHtml(optionTitle)}</option>`;
      }).join('');

      const imageMarkup = variant.image || product.image
        ? `<img src="${escapeHtml(variant.image || product.image)}" alt="${escapeHtml(product.imageAlt || product.title)}" loading="lazy">`
        : '<div class="lace-pack-builder__item-image-placeholder"></div>';

      return `
        <div class="lace-pack-builder__item-group" data-row-id="${escapeHtml(row.id)}">
          <div class="lace-pack-builder__item">
            <div class="lace-pack-builder__item-media">
              ${imageMarkup}
            </div>
            <div class="lace-pack-builder__item-body">
              <div class="lace-pack-builder__item-title">${escapeHtml(product.title)}</div>
              <div class="lace-pack-builder__item-price-row">
                <span class="lace-pack-builder__item-price" data-lace-pack-builder-row-price>${escapeHtml(formatMoney(pricing.discountedSubtotalCents))}</span>
                <span class="lace-pack-builder__item-price-compare${pricing.discountedSubtotalCents < pricing.subtotalCents ? '' : ' is-hidden'}" data-lace-pack-builder-row-compare-price>${pricing.discountedSubtotalCents < pricing.subtotalCents ? escapeHtml(formatMoney(pricing.subtotalCents)) : ''}</span>
              </div>
              <div class="lace-pack-builder__item-controls">
                <div class="${selectorClass}"${selectorStyle}>
                  ${selectorLabel ? `<span class="lace-pack-builder__select-label">${escapeHtml(selectorLabel)}</span>` : ''}
                  <select class="lace-pack-builder__select" data-lace-pack-builder-variant>
                    ${variantOptions}
                  </select>
                </div>
                <div class="lace-pack-builder__quantity" role="presentation">
                  <div class="js-qty__wrapper">
                    <input type="text" class="js-qty__num" value="${escapeHtml(row.quantity)}" min="1" aria-label="quantity" pattern="[0-9]*">
                    <button type="button" class="js-qty__adjust js-qty__adjust--minus" aria-label="Reduce quantity">
                      <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" viewBox="0 0 20 20"><path fill="#444" d="M17.543 11.029H2.1A1.032 1.032 0 0 1 1.071 10c0-.566.463-1.029 1.029-1.029h15.443c.566 0 1.029.463 1.029 1.029 0 .566-.463 1.029-1.029 1.029z"/></svg>
                      <span class="icon__fallback-text" aria-hidden="true">&minus;</span>
                    </button>
                    <button type="button" class="js-qty__adjust js-qty__adjust--plus" aria-label="Increase quantity">
                      <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" viewBox="0 0 20 20"><path fill="#444" d="M17.409 8.929h-6.695V2.258c0-.566-.506-1.029-1.071-1.029s-1.071.463-1.071 1.029v6.671H1.967C1.401 8.929.938 9.435.938 10s.463 1.071 1.029 1.071h6.605V17.7c0 .566.506 1.029 1.071 1.029s1.071-.463 1.071-1.029v-6.629h6.695c.566 0 1.029-.506 1.029-1.071s-.463-1.071-1.029-1.071z"/></svg>
                      <span class="icon__fallback-text" aria-hidden="true">+</span>
                    </button>
                  </div>
                </div>
                <button type="button" class="lace-pack-builder__remove" data-lace-pack-builder-remove data-row-id="${escapeHtml(row.id)}" aria-label="Remove item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M11.7461 5C12.0775 5 12.3467 5.26824 12.3467 5.59961V12.5996C12.3467 13.4833 11.6297 14.1993 10.7461 14.1993H3.45703C2.57357 14.199 1.85742 13.4832 1.85742 12.5996V5.59961C1.85742 5.26838 2.12586 5.00023 2.45703 5C2.7884 5 3.05664 5.26824 3.05664 5.59961V12.5996C3.05664 12.8204 3.23631 12.9998 3.45703 13H10.7461C10.967 13 11.1465 12.8206 11.1465 12.5996V5.59961C11.1465 5.26845 11.415 5.00034 11.7461 5ZM8.88867 0C9.77231 0 10.4892 0.715952 10.4892 1.59961V2.59961C10.4892 2.8203 10.6681 2.99964 10.8887 3H13.6045L13.7256 3.01172C13.999 3.06769 14.2041 3.30968 14.2041 3.59961C14.2041 3.88954 13.999 4.13153 13.7256 4.1875L13.6045 4.19922H0.599609C0.268449 4.19897 0 3.93083 0 3.59961C0 3.26839 0.268449 3.00024 0.599609 3H3.31543C3.53629 2.99994 3.71484 2.82048 3.71484 2.59961V1.59961C3.71484 0.716102 4.43198 0.000241 5.31543 0H8.88867ZM5.31543 1.19922C5.09472 1.19946 4.91504 1.37884 4.91504 1.59961V2.59961C4.91504 2.73803 4.89631 2.87192 4.86328 3H9.34181C9.30871 2.87191 9.28911 2.73803 9.28911 2.59961V1.59961C9.28911 1.37869 9.10961 1.19922 8.88867 1.19922H5.31543Z" fill="black"/></svg>
                </button>
              </div>
            </div>
          </div>
          <div class="lace-pack-builder__item-divider"></div>
        </div>
      `;
    }

    applyRowDatasets(rowElement, row) {
      if (!rowElement || !row) {
        return;
      }

      const pricing = this.getRowPricing(row, this.summary?.discountPercent || 0);
      rowElement.dataset.quantity = String(row.quantity);
      rowElement.dataset.variantId = String(row.variantId);
      rowElement.dataset.unitPriceCents = String(pricing.unitPriceCents);
      rowElement.dataset.compareAtPriceCents = String(pricing.compareUnitPriceCents);
      rowElement.dataset.subtotalCents = String(pricing.subtotalCents);
      rowElement.dataset.discountedSubtotalCents = String(pricing.discountedSubtotalCents);
      rowElement.dataset.bundleDiscountCents = String(pricing.bundleDiscountCents);

      const priceMount = rowElement.querySelector('[data-lace-pack-builder-row-price]');
      if (priceMount) {
        priceMount.textContent = formatMoney(pricing.discountedSubtotalCents);
      }

      const comparePriceMount = rowElement.querySelector('[data-lace-pack-builder-row-compare-price]');
      if (comparePriceMount) {
        const hasBundleDiscount = pricing.discountedSubtotalCents < pricing.subtotalCents;
        comparePriceMount.textContent = hasBundleDiscount ? formatMoney(pricing.subtotalCents) : '';
        comparePriceMount.classList.toggle('is-hidden', !hasBundleDiscount);
      }

      const subtotalMount = rowElement.querySelector('[data-lace-pack-builder-row-subtotal]');
      if (subtotalMount) {
        subtotalMount.textContent = formatMoney(pricing.discountedSubtotalCents);
      }
    }

    updateDerivedState() {
      const totalQuantity = this.state.rows.reduce((sum, row) => sum + Math.max(1, Number(row.quantity) || 1), 0);
      const activeTier = this.getActiveTier(totalQuantity);
      const nextTier = this.getNextTier(totalQuantity);
      const discountPercent = activeTier ? activeTier.discount : 0;
      const rows = this.state.rows.map((row) => ({
        ...row,
        pricing: this.getRowPricing(row, discountPercent)
      }));

      const subtotalCents = rows.reduce((sum, row) => sum + row.pricing.subtotalCents, 0);
      const compareSubtotalCents = rows.reduce((sum, row) => sum + row.pricing.compareSubtotalCents, 0);
      const previewDiscountCents = rows.reduce((sum, row) => sum + row.pricing.bundleDiscountCents, 0);
      const discountedSubtotalCents = subtotalCents - previewDiscountCents;
      const minimumTotalQuantity = Number(this.config.minimumTotalQuantity || 0);

      this.summary = {
        rows,
        totalQuantity,
        subtotalCents,
        compareSubtotalCents,
        previewDiscountCents,
        discountedSubtotalCents,
        discountPercent,
        activeTier,
        nextTier,
        minimumTotalQuantity,
        isValid: rows.length > 0 && totalQuantity >= minimumTotalQuantity
      };

      this.dataset.totalQuantity = String(totalQuantity);
      this.dataset.subtotalCents = String(subtotalCents);
      this.dataset.discountedSubtotalCents = String(discountedSubtotalCents);
      this.dataset.discountPercent = String(discountPercent);

      this.toggleItemsVisibility();
      this.updateProgressUI();
      this.updateSubmitButton();
      this.updateStatus();
      this.updateFutureMounts();

      this.itemsContainer?.querySelectorAll('[data-row-id]').forEach((rowElement) => {
        const row = this.state.rows.find((entry) => entry.id === rowElement.dataset.rowId);
        this.applyRowDatasets(rowElement, row);
      });

      this.dispatchEvent(new CustomEvent('lace-pack-builder:change', {
        detail: { summary: this.summary }
      }));
    }

    toggleItemsVisibility() {
      if (!this.itemsContainer) {
        return;
      }

      const hasRows = this.state.rows.length > 0;
      this.itemsContainer.hidden = !hasRows;
      if (this.summaryWrap) {
        this.summaryWrap.hidden = !hasRows;
      }
      this.classList.toggle('has-items', hasRows);
      this.classList.toggle('is-empty', !hasRows);
    }

    updateProgressUI() {
      if (this.progressNote) {
        if (this.summary.nextTier) {
          const remaining = Math.max(this.summary.nextTier.quantity - this.summary.totalQuantity, 0);
          this.progressNote.innerHTML = `${escapeHtml(this.config.progressPrefix || 'Add')} <strong>${remaining}</strong> ${escapeHtml(this.config.progressMiddle || 'more for')} <strong>${escapeHtml(`${this.summary.nextTier.discount}% OFF`)}</strong>`;
        } else if (this.summary.activeTier) {
          this.progressNote.textContent = this.config.progressCompleteText || 'Best bundle discount unlocked';
        } else {
          this.progressNote.textContent = '';
        }
      }

      if (!this.progressTrack) {
        return;
      }

      const steps = this.progressTrack.querySelectorAll('.lace-pack-builder__progress-step');
      const lines = this.progressTrack.querySelectorAll('.lace-pack-builder__progress-line');

      steps.forEach((step) => {
        const isInitialStep = step.dataset.progressInitial === 'true';
        const stepQuantity = Number(step.dataset.tierQuantity || 0);
        const isActive = isInitialStep || this.summary.totalQuantity >= stepQuantity;
        step.classList.toggle('is-active', isActive);
      });

      lines.forEach((line) => {
        const lineStart = Number(line.dataset.tierStart || 0);
        const lineEnd = Number(line.dataset.tierEnd || 0);
        const lineRange = Math.max(lineEnd - lineStart, 1);
        const progressValue = ((this.summary.totalQuantity - lineStart) / lineRange) * 100;
        const fillPercent = Math.max(0, Math.min(progressValue, 100));

        line.style.setProperty('--lace-pack-builder-progress', `${fillPercent}%`);
        line.classList.toggle('is-active', fillPercent >= 100);
      });
    }

    updateSubmitButton() {
      if (!this.submitButton) {
        return;
      }

      const label = this.summary.isValid
        ? (this.config.submitButtonLabel || 'ADD TO CART')
        : (this.config.submitButtonDisabledLabel || this.config.submitButtonLabel || 'CHOOSE PRODUCTS');

      this.submitButton.textContent = label;
      this.submitButton.disabled = !this.summary.isValid || this.isSubmitting;
      this.submitButton.classList.toggle('is-loading', this.isSubmitting);
    }

    updateStatus(message = '') {
      if (!this.statusElement) {
        return;
      }

      if (!message) {
        this.statusElement.hidden = true;
        this.statusElement.textContent = '';
        this.statusElement.classList.remove('has-error');
        return;
      }

      this.statusElement.hidden = false;
      this.statusElement.textContent = message;
      this.statusElement.classList.add('has-error');
    }

    updateFutureMounts() {
      const subtotalMount = this.querySelector('[data-lace-pack-builder-subtotal]');
      if (subtotalMount) {
        subtotalMount.textContent = formatMoney(this.summary.subtotalCents);
      }

      const compareSubtotalMount = this.querySelector('[data-lace-pack-builder-compare-subtotal]');
      if (compareSubtotalMount) {
        compareSubtotalMount.textContent = formatMoney(this.summary.compareSubtotalCents);
      }

      const savingsMount = this.querySelector('[data-lace-pack-builder-savings]');
      if (savingsMount) {
        savingsMount.textContent = formatMoney(this.summary.previewDiscountCents);
      }

      const savingsRow = this.querySelector('[data-lace-pack-builder-savings-row]');
      if (savingsRow) {
        savingsRow.hidden = this.summary.previewDiscountCents <= 0;
      }

      const totalMount = this.querySelector('[data-lace-pack-builder-total]');
      if (totalMount) {
        totalMount.textContent = formatMoney(this.summary.discountedSubtotalCents);
      }
    }

    closeModal() {
      this.modal?.querySelector('.js-modal-close')?.click();
    }

    async submitRows() {
      if (!this.summary?.isValid || this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;
      this.updateSubmitButton();
      this.updateStatus('');

      const items = this.state.rows.map((row) => ({
        id: Number(row.variantId),
        quantity: Math.max(1, Number(row.quantity) || 1),
        properties: {
          '_lace_pack_builder': this.config.blockId,
          '_lace_pack_builder_discount': String(this.summary.discountPercent),
          '_lace_pack_builder_total_quantity': String(this.summary.totalQuantity)
        }
      }));

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const errorResponse = await response.json().catch(() => null);
          throw new Error(errorResponse?.description || 'Unable to add lace pack items to cart.');
        }

        await response.json();

        document.dispatchEvent(new CustomEvent('cart:build'));
        document.querySelectorAll('.cart-link__bubble').forEach((bubble) => {
          bubble.classList.add('cart-link__bubble--visible');
        });
      } catch (error) {
        this.updateStatus(error.message || 'Unable to add lace pack items to cart.');
      } finally {
        this.isSubmitting = false;
        this.updateSubmitButton();
      }
    }
  }

  if (!window.customElements.get('lace-pack-builder')) {
    window.customElements.define('lace-pack-builder', LacePackBuilder);
  }
})();